import { useMemo, useState } from 'react'
import { useLayoutEffect } from 'react'
import { Calendar } from '@mantine/dates'
import { DateTime } from 'luxon'
import styled from 'styled-components'
import { Border, Colors, Spacing, Typography } from '../../styles'
import { icons } from '../../styles/images'
import { getFormattedDate } from '../../utils/utils'
import { Icon } from '../atoms/Icon'
import GTButton from '../atoms/buttons/GTButton'
import GTPopover from '../radix/GTPopover'

const CALENDAR_DAY_SIZE = '32px'

const GTDatePickerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    .mantine-Calendar-calendarHeaderLevel {
        text-transform: uppercase;
        ${Typography.body.small};
    }
`
const DateViewContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: ${Spacing._8};
    padding: ${Spacing._4} ${Spacing._8};
    gap: ${Spacing._8};
    border-radius: ${Border.radius.small};
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    background-color: ${Colors.background.base};
`
const DateViewText = styled.span`
    ${Typography.body.medium};
    flex: 1;
`

interface GTDatePickerProps {
    initialDate: DateTime
    setDate: (date: string) => void
    showIcon?: boolean
    onlyCalendar?: boolean
    disabled?: boolean
    // clearing emits the epoch date, which only makes sense where the date is optional
    showClearButton?: boolean
}
const GTDatePicker = ({
    initialDate,
    setDate,
    showIcon = true,
    onlyCalendar = false,
    disabled,
    showClearButton = true,
}: GTDatePickerProps) => {
    const [currentDate, setCurrentDate] = useState<DateTime | null>(initialDate)
    const formattedDate = useMemo(() => getFormattedDate(currentDate), [currentDate])

    useLayoutEffect(() => {
        if (!currentDate) return
        if (+currentDate !== +initialDate) {
            setCurrentDate(initialDate)
        }
    }, [initialDate])

    const handleOnChange = (date: Date | null) => {
        if (!date) {
            setDate(DateTime.fromMillis(0).toFormat('yyyy-MM-dd'))
        } else {
            setCurrentDate(DateTime.fromJSDate(date))
            setDate(DateTime.fromJSDate(date).toISO())
        }
    }

    const calendar = (
        <GTDatePickerWrapper>
            <Calendar
                value={currentDate?.toJSDate()}
                onChange={handleOnChange}
                placeholder="Select a Date"
                firstDayOfWeek="sunday"
                allowLevelChange={false}
                size="sm"
                dayStyle={(date, modifiers) => {
                    if (modifiers.selected) {
                        return {
                            backgroundColor: Colors.text.ink,
                            color: Colors.text.white,
                        }
                    }
                    // Today is the one yellow cue in the picker; selection stays ink.
                    if (date.toDateString() === new Date().toDateString()) {
                        return {
                            backgroundColor: Colors.accent.yellow,
                            border: `${Border.stroke.medium} solid ${Colors.accent.yellowStroke}`,
                            color: Colors.text.ink,
                            zIndex: 1,
                        }
                    }
                    if (modifiers.outside) {
                        return {}
                    }
                    return { color: Colors.text.ink }
                }}
                styles={{
                    calendarBase: {
                        maxWidth: 'none',
                    },
                    calendarHeaderLevel: {
                        color: Colors.text.secondary,
                    },
                    day: {
                        borderRadius: '50%',
                        width: CALENDAR_DAY_SIZE,
                        height: CALENDAR_DAY_SIZE,
                        margin: Spacing._4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                }}
                renderDay={(date) => <div onMouseUp={() => handleOnChange(date)}>{date.getDate()}</div>}
            />
            {currentDate?.isValid && (
                <DateViewContainer>
                    <Icon icon={icons.calendar_blank} color="black" />
                    <DateViewText>{currentDate.toFormat('ccc LLL d y')}</DateViewText>
                    {showClearButton && (
                        <GTButton
                            styleType="icon"
                            tooltipText="Remove due date"
                            icon={icons.x}
                            onClick={() => handleOnChange(null)}
                        />
                    )}
                </DateViewContainer>
            )}
        </GTDatePickerWrapper>
    )

    if (onlyCalendar) return calendar

    return (
        <div>
            <GTPopover
                content={calendar}
                disabled={disabled}
                align="start"
                trigger={
                    <GTButton
                        styleType="control"
                        icon={showIcon ? icons.clock : undefined}
                        value={formattedDate.dateString}
                        textColor={formattedDate.textColor}
                    />
                }
            />
        </div>
    )
}

export default GTDatePicker
